import "./EditDocument.scss";
import "react-toastify/dist/ReactToastify.css";
import { PreviewPane } from "@components/PreviewPane/PreviewPane";
import React, { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import { Buffer } from "buffer/";
import { Commandbar } from "@components/CommandBar/Commandbar";
import { Header } from "@components/Header/Header";
import { ModuleList } from "@components/ModuleList/ModuleList";
import { Sidebar } from "@components/Sidebar/Sidebar";
import { ToastContainer, toast } from "react-toastify";
import { applyPreviewStyles, parseModulesFromResponse, prepareModulesForSave, downloadPdf, findVariables } from "@services/utils";
import { IEditableModuleStates, IModule, IModuleList, IRawModuleList } from "@src/types";
import { useApiService } from "@services/ApiService";

const EditDocument: React.FC = () => {
    const { post } = useApiService();
    const [previewSrc, setPreviewSrc] = useState<string>('');
    const [currentModuleStates, setCurrentModuleStates] = useState<IModuleList>({});
    const [currentEditableModulesStates, setCurrentEditableModulesStates] = useState<IEditableModuleStates>({});
    const [twigVars, setTwigVars] = useState<Array<any>>([]);
    const [instanceId, setInstanceId] = useState<string | null>(null);
    const [docType, setDocType] = useState<string | null>(null);

    /**
     * Updates the current states of all modules
     * @param moduleStates (optional) the modified array of module states
     * @return The compiled HTMl
     */
    const generatePreviewData = useCallback((modulesStates: IModuleList = currentModuleStates) => {
        let srcHtml = "";
        for (let module in modulesStates) {
            if (!modulesStates[module].default) {
                continue;
            }

            srcHtml += modulesStates[module].currentHtml;
        }

        return srcHtml;
    }, [currentModuleStates]);

    /**
     * Enables editing in a module's preview div
     * @param moduleId The id of the module
     */
    const editModule = useCallback((moduleId: string) => {
        const miceText = document.getElementById(moduleId + "p");
        if (!document.getElementById(moduleId + "DIV") || !miceText) {
            return;
        }

        const currentModuleState = {...currentModuleStates[moduleId], editable: true};
        const updatedEditableModulesStates = {...currentEditableModulesStates, [moduleId]: currentModuleState};
        setCurrentEditableModulesStates(updatedEditableModulesStates);

        miceText.innerHTML = "(edited)";
    }, [currentModuleStates]);

    const editOff = useCallback((moduleId: string) => {
        const miceText = document.getElementById(moduleId + "p");
        if (!miceText) {
            return;
        }

        miceText.innerHTML = "";
    }, []);

    /**
     * Inserts a row into a module that contains a table
     * @param moduleId The id of the module
     */
    const addTableRow = useCallback((moduleId: string) => {
        if (!document.getElementById(moduleId + "DIV")) {
            return;
        }

        const table = document.getElementById(moduleId + "TABLE");
        if (!(table instanceof HTMLTableElement)) {
            return;
        }

        const numCells = table.rows[0].cells.length;
        table.insertRow();

        for (let i = 0; i < numCells; i++) {
            let cell = table.rows[table.rows.length - 1].insertCell(i);
            cell.innerHTML = "EMPTY";
        }

        const updatedModuleState: IModule = {...currentModuleStates[moduleId], currentHtml: document.getElementById( moduleId + "DIV")?.innerHTML};
        const updatedModuleStates = {
            ...currentModuleStates,
            [moduleId]: updatedModuleState
        }

        const html = generatePreviewData(updatedModuleStates);
        setCurrentModuleStates(updatedModuleStates);
        setPreviewSrc(html);

        editModule(moduleId);
    }, [currentModuleStates, previewSrc, generatePreviewData, editModule]);

    /**
     * initializes the states on the first load
     * @param modulesStates The states of the modules to set
     * @param editableModules The modules that are editable
     * @param docType The document type
     */
    const initLoad = useCallback((modulesStates: IModuleList, editableModules: IEditableModuleStates, docType: string) => {
        const htmlSrc = generatePreviewData(modulesStates);
        const foundVars = findVariables(htmlSrc);

        setDocType(docType);
        setTwigVars(foundVars);
        setPreviewSrc(htmlSrc);
        setCurrentModuleStates(modulesStates);
        setCurrentEditableModulesStates(editableModules);
    }, []);
    

    /**
     * Updates the existing doc reference in the db
     */
    const updateDoc = useCallback(async () => {
        if (!previewSrc) {
            toast.error("There is nothing to save.");
            return;
        }

        const encodedData = prepareModulesForSave(currentModuleStates);
        const url = window.REACT_APP_UPDATE_ENDPOINT + "id=" + instanceId;

        try {
            const response = await post(url, { body_data: encodedData });
            if (response.ok) {
                toast.success('Document Saved!');
            } else {
                toast.error('Something went wrong.');
            }
        } catch (error) {
            toast.error('Failed to save document.');
        }
    }, [currentModuleStates, previewSrc, instanceId, post]);

    /**
     * Generates and downloads PDF from current HTML content
     */
    const generatePdf = useCallback(async () => {
        if (!previewSrc) {
            alert("There is nothing in the document. Please select a module.");
            return;
        }

        const formatted_doc_type = _.kebabCase(docType);
        const html = Buffer.from(previewSrc).toString("base64");

        const url = 
            window.REACT_APP_EXPORTING_ENDPOINT +
            "doc-type=" +
            formatted_doc_type +
            "&id=" +
            instanceId;

        try {
            const response = await post(url, { html });

            if (!response.ok) {
                toast.error('Error generating PDF.');
                return;
            }

            const result = await response.text();
            downloadPdf(result, docType);
            toast.success('Document Generated!');
        } catch (error) {
            toast.error('Failed to generate PDF.');
        }
    }, [previewSrc, docType, instanceId, post]);

    /**
     * Changes the visibility of module divs
     */
    const updateVisableModules = useCallback((moduleId: string) => {
        const updatedCurrentModule: IModule = {...currentModuleStates[moduleId], default: !currentModuleStates[moduleId].default};
        const updatedModuleStates: IModuleList = {...currentModuleStates, [moduleId]: updatedCurrentModule};

        const html = generatePreviewData(updatedModuleStates);
        setPreviewSrc(html);
        setCurrentModuleStates(updatedModuleStates);
    }, [previewSrc, currentModuleStates, generatePreviewData]);

    /**
     * Resets a module to its original state
     */
    const resetModule = useCallback((moduleId: string) => {
        const updatedCurrentModule: IModule = {...currentModuleStates[moduleId], currentHtml: currentModuleStates[moduleId].baseHtml};
        const updatedModuleStates = {...currentModuleStates, [moduleId]: updatedCurrentModule};
        const html = generatePreviewData(updatedModuleStates);

        setPreviewSrc(html);
        setCurrentModuleStates(updatedModuleStates);
    }, [currentModuleStates, generatePreviewData]);

    /**
     * Updates the state of the modules and the preview source
     */
    const updateCurrentModuleStates = useCallback((divId: string, moduleId: string) => {
        var moduleDiv = document.getElementById(divId);
        if (!moduleDiv) {
            return;
        }

        const newModuleData = moduleDiv.innerHTML;
        const updatedModuleStates: IModuleList = {...currentModuleStates, [moduleId]: {...currentModuleStates[moduleId], currentHtml: newModuleData}};
        const updatedEditableModulesStates: IEditableModuleStates = {...currentEditableModulesStates, [moduleId]: {...currentEditableModulesStates[moduleId], editable: false}};
        const html = generatePreviewData(updatedModuleStates);

        setPreviewSrc(html);
        setCurrentModuleStates(updatedModuleStates);
        setCurrentEditableModulesStates(updatedEditableModulesStates);
    }, [currentModuleStates, generatePreviewData]);


    /**
     * Finds and replaces all instances of a variable with the new values
     */
    const saveVariables = useCallback(() => {
        // creating the json mapping new values to vars. This could be done in the modal and passed as a param
        const associatedData = {};
        for (const name of twigVars) {
            const newDataElement = document.getElementById(
                name + "_NEW_VALUE"
            ) as HTMLInputElement;

            if (!newDataElement) {
                continue;
            }

            const newData = newDataElement.value ?? '';

            // preventing script of html injection
            associatedData[name] = newData
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
        }

        const updatedModulesStates = {...currentModuleStates};

        // take a look at regex to make sure that it is consistent
        for (const module in updatedModulesStates) {
            var currentHtml = updatedModulesStates[module].currentHtml;

            const regexp = /<span\sclass=['"]VARIABLE_([^<>]*)['"]\s[^><]*>/g;
            const matches = [...currentHtml.matchAll(regexp)];

            for (const match in matches) {
                const varName = matches[match][1];
                const re = new RegExp(
                    `<span\\sclass=['"]VARIABLE_${varName}['"]\\s[^><]*>[^<>]*<\/span>`,
                    "gm"
                );

                const newVariable = `<span class="VARIABLE_${varName}" contentEditable=false>${associatedData[varName]}</span>`;

                currentHtml = currentHtml.replace(re, newVariable);
            }

            updatedModulesStates[module] = {...updatedModulesStates[module], currentHtml: currentHtml};
        }

        const html = generatePreviewData(updatedModulesStates);

        setPreviewSrc(html);
        setCurrentModuleStates(updatedModulesStates);
    }, [currentModuleStates, generatePreviewData]);


    // initialization for the document.
    useEffect(() => {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        const instanceIdParam = params.get("id");

        if (!instanceIdParam) {
            toast.error("No document id provided.");
            return;
        }

        const loadDocument = async () => {
            const url = window.REACT_APP_RETRIEVAL_ENDPOINT + "id=" + instanceIdParam;

            try {
                const response = await post(url, { data: "request" });

                if (!response.ok) {
                    throw new Error("Unable to find document.");
                }

                const result = await response.json();
                const rawModules: IRawModuleList = result.modules as IRawModuleList;
                const { modules, editableModules } = parseModulesFromResponse(rawModules);

                applyPreviewStyles(result.css);
                initLoad(modules, editableModules, result.type);
            } catch (error) {
                toast.error(error instanceof Error ? error.message : "Could not connect to server");
            }
        };

        loadDocument();
        setInstanceId(instanceIdParam);
    }, [post, initLoad]);


    return (
        <div className="App">
            <div className="Sidebar">
                <Sidebar>
                    <ModuleList
                        modules={currentModuleStates}
                        addTableRow={addTableRow}
                        updateVisableModules={updateVisableModules}
                        editModule={editModule}
                        resetModule={resetModule}
                        editOff={editOff}
                    />
                </Sidebar>
            </div>
            <div className="Content">
                <Header title={_.startCase(docType)} />
                <Commandbar
                    exportFn={generatePdf}
                    saveFn={updateDoc}
                    twigVars={twigVars}
                    saveVariables={saveVariables}
                />
                <PreviewPane
                    modules={currentModuleStates}
                    editStates={currentEditableModulesStates}
                    updateCurrentModuleStates={updateCurrentModuleStates}
                    editModule={editModule}
                />
            </div>
            <ToastContainer
                position="bottom-right"
                autoClose={2000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                draggable
                pauseOnHover={false}
            />
        </div>
    );
};

export default EditDocument;
